import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI } from '../services/api';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  Menu, User, LogOut, Settings, Bell, Shield,
  LayoutDashboard, Search, MessageSquare, FileText, ChevronDown,
  Package, CheckCircle, AlertCircle, Clock, Truck, Camera, ClipboardCheck,
  AlertTriangle, Phone
} from 'lucide-react';
import SOSButtonGlobal from './SOSButtonGlobal';

// HaulLogix Shield Logo Component - Exact Match to Brand Logo
const HaulLogixLogo = () => (
  <svg width="36" height="40" viewBox="0 0 100 110" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      {/* Shield border gradient - blue to light blue/silver */}
      <linearGradient id="shieldBorder" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1E3A5F" />
        <stop offset="40%" stopColor="#3B82F6" />
        <stop offset="70%" stopColor="#60A5FA" />
        <stop offset="100%" stopColor="#94A3B8" />
      </linearGradient>
      {/* Inner shield background - dark navy */}
      <linearGradient id="innerShield" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1E3A5F" />
        <stop offset="100%" stopColor="#0F172A" />
      </linearGradient>
      {/* Blue bar gradient - left side */}
      <linearGradient id="blueBar" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#38BDF8" />
        <stop offset="30%" stopColor="#3B82F6" />
        <stop offset="100%" stopColor="#1D4ED8" />
      </linearGradient>
      {/* Green bar gradient - right side */}
      <linearGradient id="greenBar" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#4ADE80" />
        <stop offset="30%" stopColor="#22C55E" />
        <stop offset="100%" stopColor="#15803D" />
      </linearGradient>
      <filter id="navGlow">
        <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    {/* Shield outer border with glow */}
    <path 
      d="M50 5L10 22V55C10 82 50 105 50 105C50 105 90 82 90 55V22L50 5Z" 
      fill="url(#shieldBorder)"
      filter="url(#navGlow)"
    />
    {/* Shield inner dark area */}
    <path 
      d="M50 12L16 27V55C16 78 50 98 50 98C50 98 84 78 84 55V27L50 12Z" 
      fill="url(#innerShield)"
    />
    {/* Blue vertical bar (left) - forms left of H */}
    <path d="M26 30 L26 78 L40 72 L40 36 Z" fill="url(#blueBar)" />
    {/* Green vertical bar (right) - forms right of H */}
    <path d="M60 36 L60 72 L74 78 L74 30 Z" fill="url(#greenBar)" />
    {/* White H crossbar connecting the bars */}
    <rect x="36" y="48" width="28" height="12" fill="#FFFFFF" opacity="0.95" />
  </svg>
);

export const Navbar = () => {
  const { isAuthenticated, user, profile, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadNotifications = async () => {
    try {
      const [countRes, listRes] = await Promise.all([
        notificationsAPI.getUnreadCount(),
        notificationsAPI.getAll()
      ]);
      setUnreadCount(countRes.data.unread_count);
      setNotifications(listRes.data.slice(0, 5)); // Show latest 5
    } catch (error) {
      console.error('Failed to load notifications');
    }
  };

  const handleMarkAsRead = async (notifId) => {
    try {
      await notificationsAPI.markAsRead(notifId);
      loadNotifications();
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'load_request_submitted':
        return <Package className="w-4 h-4 text-blue-400" />;
      case 'load_request_approved':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'load_request_declined':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'document_reviewed':
        return <FileText className="w-4 h-4 text-[#4ADE80]" />;
      default:
        return <Bell className="w-4 h-4 text-gray-400" />;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/dashboard', label: 'Dashboard' },
    { href: '/loadboard', label: 'Find Loads' },
    { href: '/tms/dispatch', label: 'Dispatch', auth: true },
    { href: '/tms/invoices', label: 'Invoices', auth: true },
    { href: '/alerts', label: 'Alerts', auth: true },
    { href: '/intel-map', label: 'Intel Map' },
    { href: '/directory', label: 'Directory' },
    { href: '/backlot', label: 'Community' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0D1B2A] border-b border-[#3B82F6]/30" style={{boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <HaulLogixLogo />
            <span className="text-lg font-bold tracking-wide">
              <span className="text-white">Haul</span><span className="text-[#4ADE80]">Logix</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              if (link.auth && !isAuthenticated) return null;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`px-4 py-2 text-sm font-bold transition-all rounded-lg ${
                    isActive(link.href)
                      ? 'text-[#4ADE80] bg-[#4ADE80]/10'
                      : 'text-white hover:text-[#4ADE80] hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                {/* Notifications Dropdown */}
                <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
                  <DropdownMenuTrigger asChild>
                    <button className="relative p-1" data-testid="notifications-btn">
                      <Bell className="w-5 h-5 text-gray-400 hover:text-white transition-colors" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] flex items-center justify-center text-white font-medium">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-80 bg-[#112240] border-[#3B82F6]/20 max-h-96 overflow-y-auto" style={{boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2)'}}>
                    <div className="px-4 py-3 flex items-center justify-between border-b border-[#3B82F6]/20">
                      <span className="text-sm font-semibold text-white">Notifications</span>
                      {unreadCount > 0 && (
                        <span className="text-xs font-medium text-[#4ADE80] bg-[#4ADE80]/10 px-2 py-0.5 rounded-full">{unreadCount} new</span>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="px-4 py-10 text-center">
                        <Bell className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.id}
                          className={`px-4 py-4 border-b border-[#3B82F6]/10 hover:bg-[#1E3A5F]/50 cursor-pointer transition-colors ${
                            !notif.is_read ? 'bg-[#1E3A5F]/30' : ''
                          }`}
                          onClick={() => {
                            if (!notif.is_read) handleMarkAsRead(notif.id);
                            setNotifOpen(false);
                            navigate('/notifications');
                          }}
                        >
                          <div className="flex gap-3">
                            <div className="mt-0.5">
                              {getNotificationIcon(notif.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notif.is_read ? 'text-white font-medium' : 'text-gray-300'}`}>
                                {notif.title}
                              </p>
                              <p className="text-xs text-gray-500 truncate mt-1">{notif.body}</p>
                              <p className="text-xs text-gray-600 mt-2 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(notif.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            {!notif.is_read && (
                              <div className="w-2.5 h-2.5 bg-[#4ADE80] rounded-full mt-2" style={{boxShadow: '0 0 8px rgba(74, 222, 128, 0.5)'}}></div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                    <div className="px-4 py-3">
                      <Link 
                        to="/notifications" 
                        className="text-xs font-medium text-[#60A5FA] hover:text-[#93C5FD] block text-center transition-colors"
                        onClick={() => setNotifOpen(false)}
                      >
                        View All Notifications
                      </Link>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2.5 text-sm text-gray-400 hover:text-white transition-colors" data-testid="user-menu-btn">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#4ADE80] p-0.5">
                        <div className="w-full h-full rounded-full bg-[#0D1B2A] flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      </div>
                      <span className="hidden sm:block font-medium">
                        {profile?.full_name?.split(' ')[0] || 'User'}
                      </span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 bg-[#112240] border-[#3B82F6]/20" style={{boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2)'}}>
                    <div className="px-4 py-3 border-b border-[#3B82F6]/20">
                      <p className="text-sm font-semibold text-white">{profile?.full_name}</p>
                      <p className="text-xs text-gray-500 capitalize mt-0.5">{user?.role?.replace('_', ' ')}</p>
                    </div>
                    <div className="py-1">
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} className="text-gray-300 focus:bg-[#1E3A5F] focus:text-white py-2.5">
                        <LayoutDashboard className="w-4 h-4 mr-3" />
                        Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/profile')} className="text-gray-300 focus:bg-[#1E3A5F] focus:text-white py-2.5">
                        <User className="w-4 h-4 mr-3" />
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/settings')} className="text-gray-300 focus:bg-[#1E3A5F] focus:text-white py-2.5">
                        <Settings className="w-4 h-4 mr-3" />
                        Settings
                      </DropdownMenuItem>
                    </div>
                    {/* Driver Tools - Only show for carriers/owner-operators */}
                    {(user?.role === 'carrier' || user?.role === 'owner_operator') && (
                      <>
                        <DropdownMenuSeparator className="bg-[#3B82F6]/20" />
                        <div className="px-3 py-2">
                          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Driver Tools</p>
                        </div>
                        <DropdownMenuItem onClick={() => navigate('/driver')} className="text-gray-300 focus:bg-[#1E3A5F] focus:text-white py-2.5">
                          <Truck className="w-4 h-4 mr-3" />
                          Driver Dashboard
                        </DropdownMenuItem>
                      </>
                    )}
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="text-gray-300 focus:bg-[#1E3A5F] focus:text-white py-2.5">
                        <Shield className="w-4 h-4 mr-3" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-[#3B82F6]/20" />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:bg-[#1E3A5F] focus:text-red-400 py-2.5">
                      <LogOut className="w-4 h-4 mr-3" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-white hover:text-[#4ADE80] hover:bg-white/5 font-bold" data-testid="login-btn">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-gradient-to-r from-[#4ADE80] to-[#22C55E] hover:from-[#86EFAC] hover:to-[#4ADE80] text-black font-bold px-5" style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.4)'}} data-testid="register-btn">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-[#0D1B2A] border-[#3B82F6]/20">
                <div className="flex flex-col gap-2 mt-8">
                  {navLinks.map((link) => {
                    if (link.auth && !isAuthenticated) return null;
                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setMobileOpen(false)}
                        className={`px-4 py-3 rounded-lg text-sm font-bold transition-all ${
                          isActive(link.href)
                            ? 'text-[#4ADE80] bg-[#4ADE80]/10'
                            : 'text-white hover:text-[#4ADE80] hover:bg-[#1E3A5F]'
                        }`}
                      >
                        {link.label}
                      </Link>
                    );
                  })}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      
      {/* Global SOS Button - Always visible, no login required */}
      <SOSButtonGlobal />
    </nav>
  );
};

export default Navbar;
